import React, { useState } from "react";
import { Button, ColorPicker, Input, InputNumber, Typography } from "antd";
import Tooltip from "../../../../Common/Tooltip";
import clsx from "clsx";
import {
  BorderBottomOutlined,
  BorderHorizontalOutlined,
  BorderOuterOutlined,
  BorderLeftOutlined,
  BorderRightOutlined,
  BorderTopOutlined,
  RadiusBottomleftOutlined,
  RadiusBottomrightOutlined,
  RadiusUpleftOutlined,
  RadiusUprightOutlined,
  RadiusSettingOutlined,
  ColumnHeightOutlined,
  ColumnWidthOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Block, Corners, Edges, evenCorners, isLinkValid } from "../../blocks";

import styles from "./index.module.scss";

/** One numbered input in a split field: what it is, and what it stands for. */
interface Slot {
  key: string;
  /** i18n key under `pages.campaigns.builder`. */
  label: string;
  icon: React.ReactNode;
  value: number;
  onChange: (value: number) => void;
}

/**
 * A measurement a section keeps the same all round until it does not: shown as
 * one field, or two, and expanded to one per side or per corner.
 *
 * Both the padding and the corner fields are this shape — the same grid, the
 * same tooltips, the same toggle at the end — and were written twice, which is
 * how they came to differ in small ways. The differences that are real are the
 * arguments: which slots there are collapsed and expanded, how far a number may
 * go, and what collapsing settles the values on.
 *
 * The field expands on its own when the values already differ, which is what
 * happens when a section set that way is opened again.
 *
 * The icons are antd's own for the axes, the edges and the corners, and they
 * carry the label — a word over each field would take four lines of a 280px
 * panel to say what an icon says at a glance. The full name is in the tooltip
 * and the aria-label.
 */
function SplitField({
  even,
  max,
  each,
  together,
  toggleIcon,
  apartLabel,
  togetherLabel,
  onCollapse,
}: {
  /** Whether the values still agree; when they do not, the field cannot close. */
  even: boolean;
  max: number;
  /** One field per side or per corner. */
  each: Slot[];
  /** The one or two fields shown while the values agree. */
  together: Slot[];
  toggleIcon: React.ReactNode;
  apartLabel: string;
  togetherLabel: string;
  /** Settles the values on what the collapsed fields show. */
  onCollapse: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const showEach = expanded || !even;
  const shown = showEach ? each : together;
  const label = t(
    `pages.campaigns.builder.${showEach ? togetherLabel : apartLabel}`,
  );

  const field = ({ key, label, icon, value, onChange }: Slot) => (
    <Tooltip key={key} title={t(`pages.campaigns.builder.${label}`)}>
      <InputNumber
        // A single collapsed field takes the row; two or more are laid out by
        // the grid around them.
        className={shown.length === 1 ? styles.grow : undefined}
        min={0}
        max={max}
        value={value}
        prefix={<span className={styles.icon}>{icon}</span>}
        aria-label={t(`pages.campaigns.builder.${label}`)}
        onChange={(next) => onChange(next ?? 0)}
      />
    </Tooltip>
  );

  return (
    <div className={styles.corners}>
      {shown.length === 1 ? (
        field(shown[0])
      ) : (
        <div className={styles.cornersGrid}>{shown.map(field)}</div>
      )}

      <Tooltip title={label}>
        <Button
          type="text"
          icon={toggleIcon}
          aria-label={label}
          onClick={() => {
            if (showEach) onCollapse();
            setExpanded(!showEach);
          }}
        />
      </Tooltip>
    </div>
  );
}

const EDGES: { key: keyof Edges; label: string; icon: React.ReactNode }[] = [
  { key: "top", label: "edgeTop", icon: <BorderTopOutlined /> },
  { key: "right", label: "edgeRight", icon: <BorderRightOutlined /> },
  { key: "bottom", label: "edgeBottom", icon: <BorderBottomOutlined /> },
  { key: "left", label: "edgeLeft", icon: <BorderLeftOutlined /> },
];

/**
 * Space around a section's content: a pair — above and below, then the sides —
 * or one field per side.
 *
 * Most sections keep the same space top and bottom, and the same at each side,
 * so the field opens as that pair.
 */
export function SpacingField({
  value,
  onChange,
}: {
  value: Edges;
  onChange: (value: Edges) => void;
}) {
  return (
    <SplitField
      even={value.top === value.bottom && value.left === value.right}
      max={96}
      each={EDGES.map(({ key, label, icon }) => ({
        key,
        label,
        icon,
        value: value[key],
        onChange: (next) => onChange({ ...value, [key]: next }),
      }))}
      together={[
        {
          key: "block",
          label: "paddingBlock",
          icon: <ColumnHeightOutlined />,
          value: value.top,
          onChange: (next) => onChange({ ...value, top: next, bottom: next }),
        },
        {
          key: "inline",
          label: "paddingInline",
          icon: <ColumnWidthOutlined />,
          value: value.left,
          onChange: (next) => onChange({ ...value, left: next, right: next }),
        },
      ]}
      toggleIcon={<BorderHorizontalOutlined />}
      apartLabel="paddingsApart"
      togetherLabel="paddingsTogether"
      // Collapsing has to settle on a pair, and the two the collapsed fields
      // show are the top and the left.
      onCollapse={() =>
        onChange({ ...value, bottom: value.top, right: value.left })
      }
    />
  );
}

const CORNERS: {
  key: keyof Corners;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    key: "topLeft",
    label: "cornerTopLeft",
    icon: <RadiusUpleftOutlined />,
  },
  {
    key: "topRight",
    label: "cornerTopRight",
    icon: <RadiusUprightOutlined />,
  },
  {
    key: "bottomLeft",
    label: "cornerBottomLeft",
    icon: <RadiusBottomleftOutlined />,
  },
  {
    key: "bottomRight",
    label: "cornerBottomRight",
    icon: <RadiusBottomrightOutlined />,
  },
];

/**
 * How round the corners are: one number for all four, or one per corner.
 *
 * Most sections want the same curve everywhere, and four fields to say so is
 * three too many — so the field opens as one.
 */
export function CornersField({
  value,
  onChange,
}: {
  value: Corners;
  onChange: (value: Corners) => void;
}) {
  return (
    <SplitField
      even={
        value.topLeft === value.topRight &&
        value.topLeft === value.bottomRight &&
        value.topLeft === value.bottomLeft
      }
      max={64}
      each={CORNERS.map(({ key, label, icon }) => ({
        key,
        label,
        icon,
        value: value[key],
        onChange: (next) => onChange({ ...value, [key]: next }),
      }))}
      together={[
        {
          key: "all",
          label: "cornerAll",
          icon: <BorderOuterOutlined />,
          value: value.topLeft,
          onChange: (next) => onChange(evenCorners(next)),
        },
      ]}
      toggleIcon={<RadiusSettingOutlined />}
      apartLabel="cornersApart"
      togetherLabel="cornersTogether"
      // Collapsing has to settle on one number, and the top left is the one the
      // collapsed field shows.
      onCollapse={() => onChange(evenCorners(value.topLeft))}
    />
  );
}

/**
 * The grounds a letter offers, in the order the panel shows them: from the
 * sheet outwards, palest first.
 */
const SCHEME: NonNullable<Block["bg"]>[] = [
  "Default",
  "Soft",
  "Email",
  "Accent",
  "Brand",
];

const SCHEME_LABEL: Record<NonNullable<Block["bg"]>, string> = {
  Default: "bgDefault",
  Soft: "bgSoft",
  Email: "bgEmail",
  Accent: "bgAccent",
  Brand: "bgBrand",
};

/**
 * What a section sits on: the letter's own grounds as swatches, and a colour of
 * its own beside them.
 *
 * The swatches carry the letter's actual colours rather than the words for
 * them, so a change in the letter's scheme is visible here without reading
 * anything.
 */
export function BackgroundField({
  value,
  custom,
  colours,
  onChange,
}: {
  value: NonNullable<Block["bg"]>;
  custom?: string;
  /** The letter's grounds, keyed as the swatches are. */
  colours: Record<NonNullable<Block["bg"]>, string>;
  onChange: (patch: Pick<Block, "bg" | "bgCustom">) => void;
}) {
  const { t } = useTranslation();

  // The same field the section's own colours use, with the letter's grounds as
  // its options. It was a second copy of that markup until the swatch, the
  // chequerboard behind it and antd's picker at the end had each been changed
  // in one of the two and not the other.
  return (
    <ColourField<NonNullable<Block["bg"]>>
      value={value}
      custom={custom}
      options={SCHEME.map((key) => ({
        key,
        colour: colours[key],
        label: t(`pages.campaigns.builder.${SCHEME_LABEL[key]}`),
      }))}
      onChange={({ key, custom }) =>
        // A swatch clears the section's own colour; a colour wins over whichever
        // swatch was chosen, and clearing it returns to that swatch.
        onChange(key ? { bg: key, bgCustom: undefined } : { bgCustom: custom })
      }
    />
  );
}

/**
 * One of the letter's own colours, or one of this section's own.
 *
 * The same arrangement the section's ground uses: swatches carrying the actual
 * colours, and antd's picker at the end for anything outside them. Clearing the
 * picker returns the section to whichever swatch was chosen.
 */
export function ColourField<Key extends string>({
  value,
  custom,
  options,
  onChange,
}: {
  value: Key;
  custom?: string;
  /** The colours to offer, in the order they are shown. */
  options: { key: Key; colour: string; label: string }[];
  onChange: (choice: { key?: Key; custom?: string }) => void;
}) {
  return (
    <div className={styles.swatches}>
      {options.map(({ key, colour, label }) => (
        <Tooltip key={key} title={label}>
          <button
            type="button"
            aria-pressed={!custom && value === key}
            aria-label={label}
            className={clsx(styles.swatch, {
              [styles.chosen]: !custom && value === key,
            })}
            onClick={() => onChange({ key, custom: undefined })}
          >
            <span
              className={styles.swatchColour}
              style={{ background: colour }}
            />
          </button>
        </Tooltip>
      ))}

      <ColorPicker
        allowClear
        value={custom ?? null}
        onChangeComplete={(colour) =>
          onChange({ custom: colour.toHexString() })
        }
        onClear={() => onChange({ custom: undefined })}
      />
    </div>
  );
}

/**
 * Where a section leads: a link, checked when the field is left.
 *
 * On blur rather than on every keystroke, because a link is invalid for most of
 * the time it takes to write one and a field that says so the whole way is
 * noise. Once it has been left wrong, it is checked again on every change — so
 * the message goes the moment the address is fixed, rather than making the user
 * leave the field a second time to find out. That is what antd's own Form does
 * with `validateTrigger="onBlur"`, and this is the same behaviour outside a
 * Form: the panel writes straight into the letter and has none.
 *
 * `status` is antd's own way of marking a field wrong — it is what Form.Item
 * sets on the Input when a rule fails — so the input looks exactly as it would
 * inside a Form.
 */
export function LinkField({
  value,
  onChange,
  className,
}: {
  value?: string;
  onChange: (value: string) => void;
  /** Passed through for a field the layout around it has to place. */
  className?: string;
}) {
  const { t } = useTranslation();
  const [left, setLeft] = useState(false);
  const invalid = left && !isLinkValid(value);

  return (
    <div className={clsx(styles.link, className)}>
      <Input
        status={invalid ? "error" : undefined}
        value={value}
        placeholder={t("pages.campaigns.builder.url")}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => setLeft(true)}
      />

      {invalid ? (
        <Typography.Text type="danger">
          {t("pages.campaigns.builder.linkInvalid")}
        </Typography.Text>
      ) : null}
    </div>
  );
}
